import type { UploadFile } from 'antd';
import type { AddressSearchProvider } from 'api/models/mainservice';
import type { Codebook } from 'common/models';
import { FlexRow } from 'components';
import { FormError, FormWrapper, SubmitButton } from 'components/forms/hook-form';
import { getUploadListAttachmentIds } from 'helpers/attachmentsHelpers';
import { getInitialPhoneNumberPrefix } from 'helpers/contactPersonHelpers';
import { useCustomTranslation } from 'helpers/translationHelper';
import type { CustomerKind, PhoneNumber } from 'pages/customers/models';
import React from 'react';
import { Trans } from 'react-i18next';

import { useAddPublicOrderRequestMutation } from '../queries';
import PublicOrderRequestFormFields from './PublicOrderRequestFormFields';

interface PublicOrderRequestFormProps {
  defaultKind: CustomerKind;
  tenantCode: string;
  countryId?: string;
  categories: Codebook[];
  publicFormCode: string;
  orderRequestFormRequestedDateEnabled?: boolean;
  requestedDateNote?: string | null;
  orderRequestFormServiceObjectInfoEnabled?: boolean;
  hideKindSwitch: boolean;
  onSuccess: () => void;
  addressSearchProvider: AddressSearchProvider;
}

export interface PublicOrderRequestFormValues {
  customerKind: CustomerKind;
  customerName?: string | null;
  identificationNumber?: string | null;
  firstName: string;
  lastName: string;
  phone?: PhoneNumber | null;
  email?: string | null;
  street: string;
  city: string;
  postalCode: string;
  country?: string | null;
  /** @format double */
  longitude?: number;
  /** @format double */
  latitude?: number;
  description?: string | null;
  /** @format int32 */
  estimatedDuration?: number;
  /** @format date-time */
  requestedDate?: string | null;
  /** @format uuid */
  categoryId: string;
  serviceObjectName?: string | null;
  serviceObjectIdentificationNumber?: string | null;
  attachments: UploadFile[];
  fakeStreet?: string;
}

const PublicOrderRequestForm = ({
  defaultKind,
  publicFormCode,
  countryId,
  categories,
  tenantCode,
  orderRequestFormRequestedDateEnabled,
  requestedDateNote,
  orderRequestFormServiceObjectInfoEnabled,
  hideKindSwitch,
  onSuccess,
  addressSearchProvider
}: PublicOrderRequestFormProps) => {
  const { tCommon } = useCustomTranslation();

  const addOrderRequestMutation = useAddPublicOrderRequestMutation();

  const onSubmit = async (data: PublicOrderRequestFormValues) => {
    const valuesToSubmit = {
      ...data,
      attachments: getUploadListAttachmentIds(data.attachments || []),
      fakeStreet: undefined
    };

    await addOrderRequestMutation.mutateAsync({
      publicFormCode: publicFormCode || '',
      tenantCode: tenantCode || '',
      requestToAdd: valuesToSubmit
    });

    onSuccess();
  };

  const defaultValues = {
    customerKind: defaultKind,
    phone: {
      prefix: getInitialPhoneNumberPrefix(countryId),
      number: ''
    }
  };

  return (
    <FormWrapper<PublicOrderRequestFormValues>
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      className={`PublicOrderRequestForm${hideKindSwitch ? ' hide-kind' : ''}`}
    >
      <PublicOrderRequestFormFields
        hideKindSwitch={hideKindSwitch}
        categories={categories}
        orderRequestFormRequestedDateEnabled={orderRequestFormRequestedDateEnabled}
        requestedDateNote={requestedDateNote}
        orderRequestFormServiceObjectInfoEnabled={orderRequestFormServiceObjectInfoEnabled}
        tenantCode={tenantCode}
        addressSearchProvider={addressSearchProvider}
      />
      <FormError />
      <FlexRow noMargin>
        <div className="PublicOrderRequestForm_required-info">
          <Trans i18nKey="required_info" ns="inputs">
            <span className="required-asterisk">*</span>
            Povinná položka
          </Trans>
        </div>
        <SubmitButton label={tCommon('send')} />
      </FlexRow>
    </FormWrapper>
  );
};

export default PublicOrderRequestForm;
